import { useEffect, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface Props {
  /** The picked file to crop, or null when the dialog is closed. */
  file: File | null;
  /** Target aspect ratio (w/h), e.g. 0.8 for 4:5. */
  aspect: number;
  /** Ideal output pixels for this placement (the crop is exported at this size). */
  targetWidth: number;
  targetHeight: number;
  /** Human ratio + name for the header, e.g. "4:5" / "Listing card". */
  aspectLabel: string;
  placementName: string;
  onCancel: () => void;
  onCropped: (file: File) => void;
}

/**
 * In-browser crop-to-aspect dialog for storefront placement images. The user
 * pans/zooms behind a fixed-ratio frame; on confirm we render the selected
 * region to a canvas at the placement's ideal pixel size and hand back a File —
 * so the uploaded image is ALWAYS the exact required aspect ratio (no server
 * crop, no wrong-ratio uploads).
 */
export function ImageCropDialog({
  file,
  aspect,
  targetWidth,
  targetHeight,
  aspectLabel,
  placementName,
  onCancel,
  onCropped,
}: Props) {
  const [imageUrl, setImageUrl] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) {
      setImageUrl('');
      return;
    }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setPixels(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const confirm = async () => {
    if (!pixels || !file) return;
    setBusy(true);
    try {
      const cropped = await cropToFile(imageUrl, pixels, targetWidth, targetHeight, file);
      onCropped(cropped);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Crop to {aspectLabel} — {placementName}</DialogTitle>
          <DialogDescription>
            Drag to reposition, scroll or use the slider to zoom. The image is saved at exactly{' '}
            {targetWidth}×{targetHeight} ({aspectLabel}).
          </DialogDescription>
        </DialogHeader>

        <div className="relative h-80 w-full overflow-hidden rounded-md bg-neutral-900">
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_area, areaPixels) => setPixels(areaPixels)}
              showGrid
              restrictPosition
            />
          )}
        </div>

        <div className="flex items-center gap-3 px-1">
          <span className="text-[11px] text-muted-foreground">Zoom</span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-primary"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={confirm} disabled={!pixels || busy}>
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Crop &amp; upload
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Draw the selected source region onto a target-sized canvas → a File. */
async function cropToFile(
  imageSrc: string,
  crop: Area,
  targetWidth: number,
  targetHeight: number,
  srcFile: File,
): Promise<File> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    targetWidth,
    targetHeight,
  );
  const mime = srcFile.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, mime, 0.92));
  if (!blob) throw new Error('Could not export the cropped image');
  const base = srcFile.name.replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.${mime === 'image/png' ? 'png' : 'jpg'}`, { type: mime });
}
